import React, {useMemo, useState} from "react";
import {Button, Card, CloseButton, Dropdown, Form} from "react-bootstrap";
import {useProductOptionsQuery} from "../../../hooks/useProductOptionsQuery";
import {ProductOptionModel} from "../../../models/view/productOptionModel";
import {ProductOptionValueModel} from "../../../models/view/productOptionValueModel";
import {BsCheckLg} from "react-icons/bs";
import {useAppContext} from "../../../hooks/useAppContext";
import {RedirectType} from "../../../definitions/enums";

export const ProductOptions = () => {
    const {model: {options, breakOutEarly}} = useProductOptionsQuery();

    // If an option array is empty, return an empty fragment
    if (breakOutEarly) return <></>;

    return (
        <div className="product-options-wrapper mt-3">
            {options.map((option, index) => <Option key={index} model={option}/>)}
        </div>
    )
}

interface IOption {
    model: ProductOptionModel;
}

export const Option = ({model: {name, isThumbnailable, values}}: IOption) => {
    const {changeProduct} = useAppContext();

    // Define activeValue to be the first active value in a value array, or undefined if there are no active values
    const activeValue = useMemo<ProductOptionValueModel | undefined>(() => values.find(value => value.active), [values]);

    // If the value array is empty, return an empty fragment
    if (values.length < 1) return <></>;

    // If there is only one value in the value array, and it only has one item, return an empty fragment
    if (values.length === 1 && values[0].items.length === 1) return <></>;

    const onValueChange = (value: ProductOptionValueModel, index?: number) => {
        // If activeValue is equal to value, return
        if (activeValue === value) return;

        // If the value array is empty, return
        if (value.items.length < 1) return;

        // Destructure productId from the specified index of the value array
        // (or the first index if no index is specified)
        const {productId, primaryId, path} = value.items[index ?? 0];

        // Change the product to the specified productId
        changeProduct(productId, primaryId, path, RedirectType.Client);
    };

    return (
        <Card id="productOptions" body className="mb-3 mt-0">
            {activeValue && (
                <div className="me-2 d-block mb-1">
                    {name}: <span className="fw-bold">{activeValue.value}</span>
                </div>
            )}
            <div className="d-flex flex-wrap">
                {values.map((productOptionValue, index) =>
                    <Value key={index}
                           isThumbnailable={isThumbnailable}
                           activeValue={activeValue}
                           setActiveValue={onValueChange}
                           value={productOptionValue}/>
                )}
            </div>
        </Card>
    )
}

interface IValue {
    isThumbnailable: boolean;
    value: ProductOptionValueModel;
    activeValue?: ProductOptionValueModel;
    setActiveValue: (value: ProductOptionValueModel, index?: number) => void;
}

export const Value = ({
                          isThumbnailable,
                          activeValue,
                          setActiveValue,
                          value,
                      }: IValue) => {
    const className = `me-2 mt-2 position-relative text-dark  ${activeValue && activeValue.value === value.value ? ' active' : ''}`;
    const [isOpen, setIsOpen] = useState<boolean>(false);

    if (value.items.length > 1) {
        return (
            <Dropdown
                id="product-options-dropdown"
                show={isOpen}
                onToggle={() => setIsOpen(!isOpen)}
                aria-haspopup="true"
                aria-expanded={isOpen}>
                <Dropdown.Toggle
                    id="product-options-dropdown-btn"
                    aria-haspopup="listbox"
                    className={className}
                    style={{cursor: 'pointer'}}
                    disabled={value.disabled}
                    variant="outline-secondary"
                    size="sm"
                    aria-label={`Additional Options for ${value} DropDown`}
                >
                    {value.value}
                    {activeValue && activeValue.value === value.value && (
                        <BsCheckLg className="position-absolute top-0 end-0"/>
                    )}
                </Dropdown.Toggle>
                <Dropdown.Menu
                    className="py-0 product-options-ddmenu"
                    role="listbox"
                >
                    <Dropdown.Header className="d-flex justify-content-between align-items-center border-bottom">
                        Please select an option
                        <CloseButton
                            aria-label="Close"
                            className="fs-5"
                            onClick={() => setIsOpen(false)}
                        />
                    </Dropdown.Header>
                    <Form>
                        {value.items.map(({title}, index) => (
                            <Form.Check
                                type="radio"
                                name="product-dropdown-options"
                                id={`product-dropdown-options-${index}`}
                                label={title}
                                value={title}
                                key={index}
                                onChange={() => setActiveValue(value, index)}
                                aria-selected={(activeValue && activeValue.value === value.value)}
                                checked={(activeValue && activeValue.value === value.value)}
                                role="option"
                            />
                        ))}
                    </Form>
                </Dropdown.Menu>
            </Dropdown>
        )
    }

    return (
        <Button
            variant="outline-secondary"
            size="sm"
            className={`${className} ${(isThumbnailable && value.image) && 'isThumbnail'}`}
            disabled={value.disabled || activeValue && activeValue.value === value.value}
            onClick={() => setActiveValue(value)}
        >
            {isThumbnailable && value.image ? (
                <img
                    className="img-fluid"
                    key={value.image.filename}
                    src={value.image.url}
                    alt={value.value}
                    width={value.image.width}
                    height={value.image.height}
                    title={value.image.title}
                    loading="lazy"
                    decoding="async"/>
            ) : value.value}
            {activeValue && activeValue.value === value.value &&
                <BsCheckLg className="position-absolute top-0 end-0"/>}
        </Button>
    )
}